import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  RefreshControl,
  ScrollView,
  StyleSheet,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { LinearGradient } from "expo-linear-gradient";
import * as Sentry from "@sentry/react-native";

import { PLACEHOLDER_DATA } from "@/screens/Explore/ExploreFeed/data/placeholderData";
import FeedScreenSection from "@/screens/Explore/ExploreFeed/Components/FeedScreenSection";
import ExploreFeedScreenHeader from "@/screens/Explore/ExploreFeed/ExploreFeedScreenHeader";
import {
  JobStatus,
  RecommendationCategoryFeed,
  UserRecommendedCategoryFeedResponse,
} from "@/schemas/user/explore";
import { themes } from "@/Components/Styling/themeConfigs";
import RecommendationService from "@/services/user/recommendation/api";
import { AppUserSession } from "@/schemas/auth/session";
import { isPublicVariant } from "@/utils/expo";
import ActionButton from "@/Components/Buttons/ActionButton";

type Props = {
  session: AppUserSession;
  isDevTab?: boolean;
  isMarketingMode?: boolean;
};

const currentTheme = themes.morning;

function ExploreFeedScreen({
  session,
  isDevTab = false,
  isMarketingMode = false,
}: Props): React.JSX.Element {
  // Recommendation feed preview loading state
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isRefetchingRecommendationsDev, setIsRefetchingRecommendationsDev] =
    useState(false); // dev tab refetching state

  // Recommendation job refreshing and preview feed profiles
  const [isRefreshing, setIsRefreshing] = useState(false); // pull-to-refresh state
  const [userRecommendedProfilePreview, setUserRecommendedProfilePreview] =
    useState<UserRecommendedCategoryFeedResponse>(PLACEHOLDER_DATA); // use placeholder data to loading layout while fetching real data
  const recsService = useMemo(
    () => new RecommendationService(session),
    // Memoize on token only — session identity changes (e.g. unreadNotificationsCount)
    // should not recreate the service and retrigger the initial fetch
    [session.token], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const categoryHasProfiles = (
    category: RecommendationCategoryFeed,
  ): boolean => {
    return (
      Array.isArray(category.items.items) && category.items.items.length > 0
    );
  };

  // Recommendation feed preview fetching function
  const isFetchingRef = useRef(false); // to prevent multiple simultaneous fetches
  const fetchPreviewRecommendations = useCallback(async (): Promise<void> => {
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;

    try {
      const response = await recsService.getPreviewRecommendations(
        isDevTab || isMarketingMode,
      );

      const nonEmptyCategories =
        response.categories.filter(categoryHasProfiles);

      // Store response with filtered categories
      setUserRecommendedProfilePreview({
        ...response,
        categories: nonEmptyCategories,
      });
      // Set loading to false after data is fetched
      // otherwise we keep the screen in loading state with profile card loading animations
      setIsDataLoaded(true);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    } finally {
      isFetchingRef.current = false;
    }
  }, [isDevTab, isMarketingMode, recsService]);

  useEffect(() => {
    // Only fetch on initial mount when data from API is not loaded
    if (!isDataLoaded) {
      fetchPreviewRecommendations();
    }
  }, [fetchPreviewRecommendations, isDataLoaded]);

  // Polling function to check job status
  const pollJobStatus = async (
    jobId: string,
    maxAttempts = 60, // 2 minutes Check.
    intervalMs = 2000,
  ): Promise<boolean> => {
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const jobStatus = await recsService.getRecommendationJobStatus(jobId);

        if (jobStatus.status === JobStatus.COMPLETED) {
          return true;
        }

        if (jobStatus.status === JobStatus.FAILED) {
          throw new Error("Recommendation job failed");
        }

        // Job is still PENDING or RUNNING, wait before next poll
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
        attempts++;
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    }

    // Max attempts reached
    throw new Error("Recommendation job polling timeout");
  };

  // Handler for pull-to-refresh action
  const handleRecommendationJobRefresh = async (): Promise<void> => {
    if (!isDataLoaded || isRefreshing) {
      return;
    }

    if (
      userRecommendedProfilePreview &&
      userRecommendedProfilePreview.nextRefreshTime > new Date()
    ) {
      // no need to refresh
      return;
    }

    setIsRefreshing(true);
    try {
      // Start the recommendation job and get the job ID
      const jobResponse = await recsService.refreshRecommendationJob();

      // Poll until job is completed or fails
      await pollJobStatus(jobResponse.jobId);

      // Once completed, fetch the new recommendations
      await fetchPreviewRecommendations();
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <LinearGradient
      colors={currentTheme.bg}
      style={styles.gradient}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 0 }}
    >
      <SafeAreaView style={styles.safeArea} edges={["top"]}>
        <ScrollView
          style={styles.container}
          refreshControl={
            <RefreshControl
              refreshing={false} // Don't show the refreshing icon. Refreshing is shown in header.
              onRefresh={
                isDevTab || isMarketingMode
                  ? undefined
                  : !isDataLoaded
                    ? // If data is still loading state, allow pull-to-refresh to trigger data fetch.
                      // Otherwise, trigger the job refresh flow.
                      fetchPreviewRecommendations
                    : handleRecommendationJobRefresh
              }
            />
          }
        >
          <ExploreFeedScreenHeader
            categories={userRecommendedProfilePreview.categories.map(
              (feedCategoryData) => feedCategoryData.category,
            )}
            nextRefreshTime={userRecommendedProfilePreview.nextRefreshTime}
            isRefreshing={isRefreshing}
          />
          {userRecommendedProfilePreview.categories
            .sort((a, b) => a.rank - b.rank)
            .map((feedCategoryData) => {
              return (
                <FeedScreenSection
                  key={feedCategoryData.category}
                  feedCategoryData={feedCategoryData}
                  isLoading={!isDataLoaded}
                  isDevTab={isDevTab || isMarketingMode}
                />
              );
            })}
          {!isPublicVariant() && (
            <View style={styles.devRefreshButtonContainer}>
              <ActionButton
                label="Dev Refresh Recommendations"
                onPress={async () => {
                  setIsRefetchingRecommendationsDev(true);
                  try {
                    await fetchPreviewRecommendations();
                  } finally {
                    setIsRefetchingRecommendationsDev(false);
                  }
                }}
                variant="outlined"
                isDisabled={isRefetchingRecommendationsDev || isRefreshing}
                isLoading={isRefetchingRecommendationsDev}
              />
            </View>
          )}
        </ScrollView>
      </SafeAreaView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  gradient: {
    flex: 1,
  },
  safeArea: {
    flex: 1,
    backgroundColor: "transparent",
  },
  container: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: "transparent",
  },
  devRefreshButtonContainer: {
    marginBottom: 20,
    width: "90%",
    alignSelf: "center",
  },
});

export default ExploreFeedScreen;
